import { Reducer, useReducer, useContext, useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { useTranslation } from "react-i18next";
import { MainContainer } from "../../components/layout";
import AppContext from "../../state/AppContext";
import SmallLoader from "../../components/loaders/SmallLoader";
import myDashboardApi from "../../api/dashboard/myDashboardApi";
import { useAuth } from "react-oidc-context";
import MyDashboardFormsApiResponseDto from "../../types/dtos/dashboards/MyDashboardFormsApiResponseDto";
import UserContext from "../../state/UserContext";
import SearchField from "../../components/common/SearchField";
import { FormListingType } from "../../types/forms/FormListingType";
import {
  faMessagesQuestion,
  faSquarePollHorizontal,
} from "@fortawesome/pro-regular-svg-icons";
import JourneyListingDto from "../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import PageIntro from "../../components/common/PageIntro";
import JourneyFormSection from "../../components/employee-dashboard/JourneyFormSection";

type JourneyFormsCollection = JourneyListingDto[];

interface YourFormsState {
  isLoading: boolean;
  activeForms: JourneyListingDto[] | null;
  completedForms: JourneyFormsCollection | null;
  filteredActiveForms: JourneyListingDto[] | null;
  filteredCompletedForms: JourneyListingDto[] | null;
  loggedInUserCurrentJourneyRef: string | null;
}

interface YourFormsProps {
  formType: FormListingType;
}

const YourForms = ({ formType }: YourFormsProps) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const appContext = useContext(AppContext);
  const auth = useAuth();
  const dashboardApi = new myDashboardApi(auth.user?.access_token);

  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const [state, setState] = useReducer<
    Reducer<YourFormsState, Partial<YourFormsState>>
  >((state, newState) => ({ ...state, ...newState }), {
    isLoading: false,
    activeForms: null,
    completedForms: null,
    filteredActiveForms: null,
    filteredCompletedForms: null,
    loggedInUserCurrentJourneyRef: null,
  });

  useEffectOnce(() => {
    setState({ isLoading: true });

    const translationKey =
      formType === "CONVERSATIONS"
        ? "Nav.LargeScreen.MyDashboard.Conversations"
        : "Nav.LargeScreen.MyDashboard.PulseChecks";

    appContext.setPageTitle(
      t("Nav.LargeScreen.MyDashboard.Root") + ": " + t(translationKey)
    );
    appContext.setPageIcon(
      formType === "CONVERSATIONS" ? faMessagesQuestion : faSquarePollHorizontal
    );

    // // Hit the API to load the forms
    dashboardApi.getJourneyFormsData(
      formType,
      (data: MyDashboardFormsApiResponseDto) => {
        setState({
          isLoading: false,
          activeForms: data.activeForms,
          filteredActiveForms: data.activeForms,
          completedForms: data.completedForms,
          filteredCompletedForms: data.completedForms,
          loggedInUserCurrentJourneyRef: data.currentJourneyRef,
        });
      },
      (error: any) => {
        console.error("Error loading journey forms", error);
      }
    );
  });

  useEffect(() => {
    // If the forms are not loaded yet, return
    if (!state.activeForms || !state.completedForms) {
      return;
    }

    // Filter the forms based on the search term
    if (searchTerm) {
      let activeForms = state.activeForms?.filter((form) =>
        form.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      let completedForms = state.completedForms?.filter((form) =>
        form.title.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setState({
        filteredActiveForms: activeForms,
        filteredCompletedForms: completedForms,
      });
    } else {
      setState({
        filteredActiveForms: state.activeForms,
        filteredCompletedForms: state.completedForms,
      });
    }
  }, [searchTerm]);

  const handleSearchTermChange = (newsearchTerm: string | null) => {
    setSearchTerm(newsearchTerm);
  };

  const searchComponent = (
    <SearchField
      onSearch={handleSearchTermChange}
      isLoading={state.isLoading}
      loaderColor={userContext.user.client.accentHexColour}
      searchTerm={searchTerm}
      includeSetWidth={false}
      placeholderText="Search..."
      outerContainerClassName=""
    />
  );

  return (
    <MainContainer>
      {state.isLoading && (
        <div className="pt-2 lg:pt-12">
          <SmallLoader />
          <p className="text-center pt-1 text-[#959595] text-sm">
            {t("Common.Loading")}...
          </p>
        </div>
      )}
      {!state.isLoading && (
        <>
          <PageIntro
            text={t(
              formType === "CONVERSATIONS"
                ? "Pages.MyDashboard.Conversations.Intro"
                : "Pages.MyDashboard.PulseChecks.Intro"
            )}
            marginClassNames="my-2"
          />
          <div className="mt-2 lg:hidden">{searchComponent}</div>
          <JourneyFormSection
            userContext={userContext}
            activeForms={state.filteredActiveForms || []}
            completedForms={state.filteredCompletedForms || []}
            searchComponent={searchComponent}
            loggedInUserCurrentJourneyRef={state.loggedInUserCurrentJourneyRef}
          />
        </>
      )}
    </MainContainer>
  );
};

export default YourForms;
